import React, { useEffect, useState } from 'react';
import { Image, ImageUsage } from '@go-shared/types/image';
import { getImageUsage } from '@go-client/lib/api/image';
import { BubbleAlert, BubbleButton, BubbleOverlay, BubbleTable } from './BubbleUI';
import {
  BubbleTableFieldBehavior,
  BubbleTableFields,
  BubbleTableTemplate,
} from './BubbleUI/BubbleTable';
import { CopyToClipboardButton } from './CopyToClipboard';
import { resolveLibrariesPath } from '@go-client/routes';
import { useActiveProperty } from '@go-client/context/ActivePropertyContext';

interface ImageUsageProps {
  image?: Image;
}

type ImageUsageTableItem = {
  id: string;
  name: string;
  type: 'events' | 'venues' | 'content';
};

export default function ImageUsageList(props: ImageUsageProps) {
  const { activeProperty: property } = useActiveProperty();
  const [data, setData] = useState<ImageUsage | undefined | null>(undefined);

  async function fetchData() {
    if (!props.image) {
      setData(null);
      return;
    }

    try {
      const result = await getImageUsage(props.image);
      setData(result);
    } catch (error) {
      setData(null);
      throw error;
    }
  }

  useEffect(() => {
    fetchData().catch(console.error);
  }, []);

  const eventItems = data?.events.map(event => {
    const item: ImageUsageTableItem = {
      id: event._id,
      name: event.name,
      type: 'events',
    };
    return item;
  });
  const venueItems = data?.venues.map(venue => {
    const item: ImageUsageTableItem = {
      id: venue._id,
      name: venue.name,
      type: 'venues',
    };
    return item;
  });
  const contentItems = data?.content.map(content => {
    const item: ImageUsageTableItem = {
      id: content._id,
      name: content.name,
      type: 'content',
    };
    return item;
  });

  const tableFields: BubbleTableFields<ImageUsageTableItem>[] = [
    {
      key: 'name',
      label: 'Name',
    },
    {
      key: 'actions',
      label: '',
      behavior: BubbleTableFieldBehavior.Condense,
    },
  ];

  const tableTemplates: Array<BubbleTableTemplate<ImageUsageTableItem>> = [
    {
      key: 'name',
      render: item => {
        return (
          <>
            <div>
              <div>{item.name}</div>
              <CopyToClipboardButton copyText={item.id} label="Copy ID" />
            </div>
          </>
        );
      },
    },
    {
      key: 'actions',
      render: item => (
        <>
          <BubbleButton
            variant="navy"
            inverse
            icon="arrow-up-right"
            linkProps={{
              to: `${resolveLibrariesPath(property._id, item.type)}?search=${item.id}`,
            }}
          >
            Jump to item
          </BubbleButton>
        </>
      ),
    },
  ];

  return (
    <BubbleOverlay loading={data === undefined}>
      {data === null ? (
        <BubbleAlert variant="crimson">
          Error fetching image usage data. Please try again later.
        </BubbleAlert>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          <div>
            <h2 className="text-xl mb-2 text-slate">Events</h2>
            <BubbleTable
              responsive
              headVariant="titanium"
              bordered
              striped
              fields={tableFields}
              items={eventItems}
              templates={tableTemplates}
            />
          </div>
          <div>
            <h2 className="text-xl mb-2 text-slate">Venues</h2>
            <BubbleTable
              responsive
              headVariant="titanium"
              bordered
              striped
              fields={tableFields}
              items={venueItems}
              templates={tableTemplates}
            />
          </div>
          <div>
            <h2 className="text-xl mb-2 text-slate">Content</h2>
            <BubbleTable
              responsive
              headVariant="titanium"
              bordered
              striped
              items={contentItems}
              fields={tableFields}
              templates={tableTemplates}
            />
          </div>
        </div>
      )}
    </BubbleOverlay>
  );
}
